import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import {
    getCalendars,
    createCalendar as createCalendarStorage,
    updateCalendar as updateCalendarStorage,
    deleteCalendar as deleteCalendarStorage,
    shareCalendar as shareCalendarStorage,
    updateMemberPermission as updatePermissionStorage,
    removeMember as removeMemberStorage,
    leaveCalendar as leaveCalendarStorage,
    regenerateShareCode as regenerateShareCodeStorage,
    regenerateAllOwnedShareCodes as regenerateAllOwnedShareCodesStorage,
    joinCalendarByCode as joinCalendarByCodeStorage,
    getUserPermission,
    PERMISSIONS,
    CALENDAR_LIMITS
} from '../services/calendarStorage';

const CalendarContext = createContext();

export const useCalendars = () => {
    const context = useContext(CalendarContext);
    if (!context) {
        throw new Error('useCalendars must be used within a CalendarProvider');
    }
    return context;
};

export const CalendarProvider = ({ children }) => {
    const [calendars, setCalendars] = useState([]);
    const [selectedCalendar, setSelectedCalendar] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { currentUser } = useAuth();
    const hasRegeneratedCodes = useRef(false);

    // Create default "Personal" calendar for new users
    const createDefaultCalendar = useCallback(async () => {
        if (!currentUser) return;

        try {
            const defaultCalendar = await createCalendarStorage(
                {
                    name: 'Personal',
                    description: 'My personal calendar',
                    color: 'blue'
                },
                currentUser.uid
            );

            setCalendars([defaultCalendar]);
            setSelectedCalendar(defaultCalendar);
            return defaultCalendar;
        } catch (err) {
            console.error('Error creating default calendar:', err);
            setError(err.message);
        }
    }, [currentUser]);

    // Load all calendars for the current user
    const loadCalendars = useCallback(async () => {
        if (!currentUser) {
            setCalendars([]);
            setSelectedCalendar(null);
            setLoading(false);
            hasRegeneratedCodes.current = false;
            return;
        }

        try {
            setLoading(true);
            setError(null);

            // Regenerate share codes for all owned calendars on login (once per session)
            if (!hasRegeneratedCodes.current) {
                try {
                    await regenerateAllOwnedShareCodesStorage(currentUser.uid);
                    hasRegeneratedCodes.current = true;
                } catch (err) {
                    console.error('Error regenerating share codes:', err);
                    // Don't block calendar loading if regeneration fails
                }
            }

            const data = await getCalendars(currentUser.uid);

            // If no calendars exist, create a default "Personal" calendar
            if (data.length === 0) {
                await createDefaultCalendar();
                return; // createDefaultCalendar already sets state
            }

            setCalendars(data);

            // Auto-select first calendar if none selected
            setSelectedCalendar(prev => {
                if (!prev && data.length > 0) {
                    return data[0];
                } else if (prev) {
                    // Update selected calendar with fresh data
                    const updated = data.find(cal => cal.id === prev.id);
                    if (updated) {
                        return updated;
                    } else if (data.length > 0) {
                        // Selected calendar no longer exists, select first available
                        return data[0];
                    }
                }
                return prev;
            });
        } catch (err) {
            console.error('Error loading calendars:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [currentUser, createDefaultCalendar]);

    // Load calendars when user changes
    useEffect(() => {
        loadCalendars();
    }, [loadCalendars]);

    // Create a new calendar
    const createCalendar = async (calendarData, userTier = 'free') => {
        if (!currentUser) {
            throw new Error('User must be logged in to create calendars');
        }

        // Local pre-check to give immediate feedback before the Firestore write
        const ownedCount = calendars.filter(cal => cal.ownerId === currentUser.uid).length;
        const limit = CALENDAR_LIMITS[userTier] ?? CALENDAR_LIMITS.free;
        if (ownedCount >= limit) {
            const limitErr = new Error(
                userTier === 'free'
                    ? `Free accounts are limited to ${limit} calendars. Upgrade to create more.`
                    : `You have reached the maximum of ${limit} calendars.`
            );
            limitErr.code = 'calendar-limit-reached';
            throw limitErr;
        }

        try {
            const newCalendar = await createCalendarStorage(calendarData, currentUser.uid, userTier);
            setCalendars(prev => [newCalendar, ...prev]);

            // Auto-select the newly created calendar
            setSelectedCalendar(newCalendar);

            return newCalendar;
        } catch (err) {
            console.error('Error creating calendar:', err);
            throw err;
        }
    };

    // Update a calendar
    const updateCalendar = async (calendarId, updates) => {
        if (!currentUser) {
            throw new Error('User must be logged in to update calendars');
        }

        try {
            const updatedCalendar = await updateCalendarStorage(calendarId, updates, currentUser.uid);

            setCalendars(prev =>
                prev.map(cal => cal.id === calendarId ? updatedCalendar : cal)
            );

            if (selectedCalendar?.id === calendarId) {
                setSelectedCalendar(updatedCalendar);
            }

            return updatedCalendar;
        } catch (err) {
            console.error('Error updating calendar:', err);
            throw err;
        }
    };

    // Delete a calendar
    const deleteCalendar = async (calendarId) => {
        if (!currentUser) {
            throw new Error('User must be logged in to delete calendars');
        }

        try {
            await deleteCalendarStorage(calendarId, currentUser.uid);

            setCalendars(prev => prev.filter(cal => cal.id !== calendarId));

            // If deleted calendar was selected, select first available
            if (selectedCalendar?.id === calendarId) {
                const remaining = calendars.filter(cal => cal.id !== calendarId);
                setSelectedCalendar(remaining.length > 0 ? remaining[0] : null);
            }
        } catch (err) {
            console.error('Error deleting calendar:', err);
            throw err;
        }
    };

    // Share calendar with another user
    const shareCalendar = async (calendarId, targetUserId, permission) => {
        if (!currentUser) {
            throw new Error('User must be logged in to share calendars');
        }

        try {
            const updatedCalendar = await shareCalendarStorage(
                calendarId,
                targetUserId,
                permission,
                currentUser.uid
            );

            setCalendars(prev =>
                prev.map(cal => cal.id === calendarId ? updatedCalendar : cal)
            );

            if (selectedCalendar?.id === calendarId) {
                setSelectedCalendar(updatedCalendar);
            }

            return updatedCalendar;
        } catch (err) {
            console.error('Error sharing calendar:', err);
            throw err;
        }
    };

    // Update member permission
    const updateMemberPermission = async (calendarId, targetUserId, newPermission) => {
        if (!currentUser) {
            throw new Error('User must be logged in');
        }

        try {
            const updatedCalendar = await updatePermissionStorage(
                calendarId,
                targetUserId,
                newPermission,
                currentUser.uid
            );

            setCalendars(prev =>
                prev.map(cal => cal.id === calendarId ? updatedCalendar : cal)
            );

            if (selectedCalendar?.id === calendarId) {
                setSelectedCalendar(updatedCalendar);
            }

            return updatedCalendar;
        } catch (err) {
            console.error('Error updating permission:', err);
            throw err;
        }
    };

    // Remove a member from calendar
    const removeMember = async (calendarId, targetUserId) => {
        if (!currentUser) {
            throw new Error('User must be logged in');
        }

        try {
            const updatedCalendar = await removeMemberStorage(
                calendarId,
                targetUserId,
                currentUser.uid
            );

            setCalendars(prev =>
                prev.map(cal => cal.id === calendarId ? updatedCalendar : cal)
            );

            if (selectedCalendar?.id === calendarId) {
                setSelectedCalendar(updatedCalendar);
            }

            return updatedCalendar;
        } catch (err) {
            console.error('Error removing member:', err);
            throw err;
        }
    };

    // Leave a shared calendar
    const leaveCalendar = async (calendarId) => {
        if (!currentUser) {
            throw new Error('User must be logged in');
        }

        try {
            await leaveCalendarStorage(calendarId, currentUser.uid);

            setCalendars(prev => prev.filter(cal => cal.id !== calendarId));

            // If left calendar was selected, select first available
            if (selectedCalendar?.id === calendarId) {
                const remaining = calendars.filter(cal => cal.id !== calendarId);
                setSelectedCalendar(remaining.length > 0 ? remaining[0] : null);
            }
        } catch (err) {
            console.error('Error leaving calendar:', err);
            throw err;
        }
    };

    // Regenerate share code for a calendar
    const regenerateCode = async (calendarId) => {
        if (!currentUser) {
            throw new Error('User must be logged in');
        }

        try {
            const newShareCode = await regenerateShareCodeStorage(calendarId, currentUser.uid);

            // Reload calendars to get updated share code
            await loadCalendars();

            return newShareCode;
        } catch (err) {
            console.error('Error regenerating share code:', err);
            throw err;
        }
    };

    // Join a calendar using a share code
    const joinByCode = async (shareCode) => {
        if (!currentUser) {
            throw new Error('User must be logged in');
        }

        try {
            const joinedCalendar = await joinCalendarByCodeStorage(shareCode, currentUser.uid);

            // Reload calendars to include the newly joined calendar
            await loadCalendars();

            return joinedCalendar;
        } catch (err) {
            console.error('Error joining calendar by code:', err);
            throw err;
        }
    };

    // Get user's permission for a calendar
    const getPermission = async (calendarId) => {
        if (!currentUser) return null;

        try {
            return await getUserPermission(calendarId, currentUser.uid);
        } catch (err) {
            console.error('Error getting permission:', err);
            return null;
        }
    };

    // Check if user is owner of a calendar
    const isOwner = (calendar) => {
        return calendar?.ownerId === currentUser?.uid;
    };

    // Check if user can edit a calendar
    const canEdit = (calendar) => {
        if (!calendar || !currentUser) return false;
        const permission = calendar.permissions?.[currentUser.uid];
        return permission === PERMISSIONS.OWNER || permission === PERMISSIONS.EDITOR;
    };

    // Check if user can view a calendar
    const canView = (calendar) => {
        if (!calendar || !currentUser) return false;
        return calendar.members?.includes(currentUser.uid);
    };

    // Refresh calendars
    const refreshCalendars = useCallback(async () => {
        await loadCalendars();
    }, [loadCalendars]);

    const value = {
        calendars,
        selectedCalendar,
        setSelectedCalendar,
        loading,
        error,
        createCalendar,
        updateCalendar,
        deleteCalendar,
        shareCalendar,
        updateMemberPermission,
        removeMember,
        leaveCalendar,
        regenerateCode,
        joinByCode,
        getPermission,
        isOwner,
        canEdit,
        canView,
        refreshCalendars,
        PERMISSIONS
    };

    return (
        <CalendarContext.Provider value={value}>
            {children}
        </CalendarContext.Provider>
    );
};

export default CalendarContext;
